import React, { useEffect, useMemo, useState } from "react";
import { ComposeType } from "types/communication.types";

import PersonList from "components/Communication/PersonList";
import MessageView from "components/Communication/MessageView";
import AccountsPanel from "components/Communication/AccountPanel";
import ComposeModal from "components/Communication/ComposeModal";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import ThreadPanel from "components/Communication/ThreadPanel";

import { OrgSelector } from "components/Organisation/OrganisationSelector";

import { storedTokens } from "utils/storedTokens";
import { Button, Checkbox, Dropdown, Empty, Flex, message, notification, Segmented, Tooltip, Typography } from "antd";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiService } from "api/api.service";
import { TbExternalLink, TbLoader2, TbMail, TbPhone, TbRefresh } from "react-icons/tb";
import { timeAgo } from "utils/functions";
import { IChannel } from "dtos/communication-channel.dto";
import { NotFoundEmptyState } from "components/svgs/NotFoundEmptyState";
import SyncModal from "components/Communication/SyncModal";

const UnifiedCommsApp: React.FC<Partial<{ source: "org" | "app"; personId: string }>> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const person_id = props.personId ?? searchParams.get("person_id") ?? "";
  const source = props.source || searchParams.get("src");
  const isSrcOrganization = source === "org";

  // const desk_id = searchParams.get("desk_id") ?? "";
  // const orgCode = searchParams.get("orgCode");

  const { appKey } = useParams();
  const organisations = ApiService.organisation.useGetAllOrganisations();
  const [selectedOrg, setSelectedOrg] = useState(() => {
    const queryOrgCode = searchParams.get("orgCode") || storedTokens.orgCode.getToken();
    return queryOrgCode ?? undefined;
  });

  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const deskQuery = ApiService.communicationChannel.useFetchDeskInfo(selectedOrg);
  const deskId = deskQuery.data?.desk?._id;
  const fullApplication = ApiService.application.useGetByAppKey(appKey);
  const [isThreadPanelOpen, setOpenThreadPanel] = useState(false);
  const [isPersonListOpen, setIsPersonListOpen] = useState(true);
  const [showAccountsPanel, setShowAccountsPanel] = useState(false);
  const [selectedChannelType, setSelectedChannelType] = useState<"email" | "sms">("email");
  const [composeModal, setComposeModal] = useState<ComposeType>(null);
  const [syncModal, setSyncModal] = useState<IChannel | null>(null);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const orgDetailsRes = ApiService.organisation.useGetBySlug(selectedOrg as string | undefined);
  const orgDetails = orgDetailsRes.data;
  const getDeskDetails = ApiService.communicationChannel.useGetDeskByID({
    desk_id: deskId ?? null,
    refetchInterval: (query) => {
      return false;
    },
  });
  const syncChannels = ApiService.communicationChannel.useSyncChannel();
  const deskDetails = getDeskDetails.data?.result;
  const checkIdDeskExistsOrNot = ApiService.communicationChannel.useCheckDeskCorrelationExists();
  const createDesk = ApiService.communicationChannel.useCreateDesk();
  const availableEmailChannels = useMemo(
    () => deskDetails?.channels.filter((ch) => ch.type === "email"),
    [deskDetails],
  );
  const availableSmsChannels = useMemo(() => deskDetails?.channels.filter((ch) => ch.type === "sms"), [deskDetails]);

  useEffect(() => {
    setSelectedChannels((deskDetails?.channels ?? [])?.map((em) => em._id));
  }, [deskDetails]);

  const handleChannelToggle = (channelId: string, checked: boolean) => {
    if (checked) {
      setSelectedChannels([...selectedChannels, channelId]);
    } else {
      setSelectedChannels(selectedChannels.filter((id) => id !== channelId));
    }
  };
  // console.log({ selectedChannelType });

  const [focusedMessageId, setFocusedMessageId] = useState<string | null>(null);

  const checkAndCreateChannel = () => {
    checkIdDeskExistsOrNot.mutate(
      { correlationId: selectedOrg as string },
      {
        onSuccess: (res) => {
          console.log({ res });
          if (res.result.exists) {
            navigate(
              `/dashboard/organisation/${selectedOrg}/channel?desk_id=${res.result.desk._id}&orgCode=${selectedOrg}&src=org`,
              { replace: true },
            );
          } else {
            createDesk.mutate(
              {
                correlation_id: orgDetails?.code as string,
                meta: {
                  name: orgDetails?.name,
                  code: orgDetails?.code,
                  slug: orgDetails?.slug,
                  logo: orgDetails?.meta?.logo,
                  favicon: orgDetails?.meta?.favicon,
                },
              },
              {
                onSuccess: (res) => {
                  navigate(
                    `/dashboard/organisation/${selectedOrg}/channel?desk_id=${res.result._id}&orgCode=${selectedOrg}&src=org`,
                    { replace: true },
                  );
                },
              },
            );
          }
        },
      },
    );
  };

  const EmptyState = ({ message, orgCode, deskId }: { message: string; orgCode: string | null; deskId?: string }) => (
    <div>
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={message} />
    </div>
  );

  const ChannelList = ({
    channels,
    selectedChannels,
    onToggle,
    orgCode,
    deskId,
    selectedOrg,
  }: {
    channels: IChannel[];
    selectedChannels: string[];
    onToggle: (channelId: string, checked: boolean) => void;
    orgCode: string | null;
    deskId?: string;
    selectedOrg?: string;
    selectedChannelType: "email" | "sms";
  }) => (
    <div className="max-h-[240px] overflow-y-auto">
      {channels.map((channel) => {
        const channelId = channel._id;
        const isChecked = selectedChannels.includes(channelId);

        return (
          <div
            key={channelId}
            className="flex items-center gap-[10px] px-[14px] py-[9px] transition-colors hover:bg-gray-50"
            style={{ borderBottom: "1px solid #f9f9fb" }}
          >
            <Checkbox checked={isChecked} onChange={(e) => onToggle(channelId, e.target.checked)} />

            {selectedChannelType === "email" ? (
              <TbMail className="shrink-0 text-[13px] text-blue-400" />
            ) : (
              <TbPhone className="shrink-0 text-[13px] text-blue-400" />
            )}

            <div className="flex min-w-0 flex-1 flex-col">
              {channel?.alias ? (
                <div className="flex flex-col items-start">
                  <span className="truncate text-[12.5px] font-medium text-gray-800">{channel.alias}</span>
                  <span className="truncate text-[10.5px]  text-gray-400">{channel.label}</span>
                </div>
              ) : (
                <Tooltip title={`${channel.label}`}>
                  <span className="truncate text-[12.5px] font-medium text-gray-800">{channel.label}</span>
                </Tooltip>
              )}
              {channel.type === "email" && (
                <span className="text-[11px] text-gray-400">
                  {channel.lastSyncedAt ? `Synced ${timeAgo(channel.lastSyncedAt)} ago` : "Never synced"}
                </span>
              )}
            </div>

            {channel.type === "email" && (
              <Tooltip title="Sync" placement="left">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setChannelDropdownOpen(false);
                    setSyncModal(channel);
                  }}
                  className="flex h-[26px] w-[26px] shrink-0 cursor-pointer items-center justify-center rounded-[6px] text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500"
                  style={{ border: "1px solid #e5e7eb", background: "white" }}
                >
                  {syncChannels.variables?.channel_id === channel._id && syncChannels.isPending ? (
                    <TbRefresh className="text-[13px] animate-spin" />
                  ) : (
                    <TbRefresh className="text-[13px]" />
                  )}
                </button>
              </Tooltip>
            )}
          </div>
        );
      })}
    </div>
  );
  const channelDropdownContent = (
    <div
      className="w-[380px] overflow-hidden rounded-[10px] bg-white"
      style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.10)", border: "1px solid #f0f0f8" }}
    >
      {/* header */}
      <div
        className="flex items-center justify-between px-[14px] py-[10px]"
        style={{ borderBottom: "1px solid #f0f0f8" }}
      >
        <div className="flex flex-col items-start">
          <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Channels</span>
          <Button
            icon={<TbExternalLink />}
            size="small"
            iconPosition="end"
            type="link"
            onClick={checkAndCreateChannel}
            className="text-[12.5px] !pl-0 font-medium text-gray-800"
          >
            Configure channels
          </Button>
        </div>
        <Segmented
          defaultChecked
          value={selectedChannelType}
          size="small"
          options={[
            { label: "Email", value: "email" },
            { label: "SMS", value: "sms" },
          ]}
          onChange={setSelectedChannelType}
        />
      </div>

      {/* channel list - conditional rendering based on selectedChannelType */}
      {selectedChannelType === "email" ? (
        // EMAIL SECTION
        (availableEmailChannels ?? []).length === 0 ? (
          <EmptyState message="No emails channels configured" orgCode={selectedOrg as string} deskId={deskId} />
        ) : (
          <ChannelList
            channels={availableEmailChannels ?? []}
            selectedChannels={selectedChannels}
            onToggle={handleChannelToggle}
            orgCode={selectedOrg as string}
            deskId={deskId}
            selectedOrg={selectedOrg}
            selectedChannelType={selectedChannelType}
          />
        )
      ) : // SMS SECTION
      (availableSmsChannels ?? []).length === 0 ? (
        <EmptyState message="No sms channels configured" orgCode={selectedOrg as string} deskId={deskId} />
      ) : (
        <ChannelList
          channels={availableSmsChannels ?? []}
          selectedChannels={selectedChannels}
          onToggle={handleChannelToggle}
          orgCode={selectedOrg as string}
          deskId={deskId}
          selectedOrg={selectedOrg}
          selectedChannelType={selectedChannelType}
        />
      )}
    </div>
  );

  // if (!deskQuery?.data?.exists) {
  //   return (

  //   );
  // }

  return (
    <DashboardLayout
      headerRight={
        <div className="!mt-2 flex items-center gap-4">
          <Dropdown
            trigger={["click"]}
            dropdownRender={() => channelDropdownContent}
            placement="bottomRight"
            open={channelDropdownOpen}
            onOpenChange={setChannelDropdownOpen}
          >
            <Button type="default" icon={<TbMail />} style={{ border: "1px solid #e5e7eb", position: "relative" }}>
              Channels
              {selectedChannels.length > 0 && (
                <span className="flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-purple-500 px-[4px] text-[10px] font-bold text-white">
                  {selectedChannels.length}
                </span>
              )}
            </Button>
          </Dropdown>
          {isSrcOrganization ? (
            <OrgSelector
              organisations={organisations.data ?? []}
              selected={selectedOrg}
              onChange={(org) => {
                setSearchParams((prev) => ({ ...prev, orgCode: org.code }));
                setSelectedOrg(org.code);
              }}
            />
          ) : null}
        </div>
      }
    >
      {source === "org" ? (
        <PageTitle title="Conversations" pageTitle={[{ title: "Communications" }, { title: "Conversations" }]} />
      ) : (
        <PageTitle
          title="Communication"
          pageTitle={[
            { title: "Applications", link: `/dashboard/applications` },
            {
              title:
                fullApplication.data?.data?.application?.client_details?.name ??
                fullApplication.data?.application?.meta?.client_name ??
                "Application Item",
              link: `/dashboard/application/${appKey}`,
            },
            {
              title: "Communication",
            },
          ]}
        />
      )}
      {deskQuery.isLoading ? (
        <div className="flex items-center justify-center h-[95vh]">
          <TbLoader2 size={64} className="animate-spin stroke-purple-600" />
        </div>
      ) : (
        <div className="flex h-[95vh] w-full overflow-hidden">
          {isSrcOrganization && (
            <PersonList isOpen={isPersonListOpen} deskId={deskId} closePanel={() => setIsPersonListOpen(false)} />
          )}

          {!deskQuery.data?.exists ? (
            <div className="flex-1 flex h-full items-center justify-center">
              <NotFoundEmptyState
                title="Desk not found"
                subtitle="We couldn't find this desk. Please check the URL or navigate to your desk list."
                actionBtn={
                  <Button
                    icon={<TbExternalLink />}
                    iconPosition="end"
                    onClick={checkAndCreateChannel}
                    // href={`/dashboard/organisation/${orgCode}/channel?desk_id=${desk_id}&orgCode=${orgCode}&src=org`}
                  >
                    Configure Desk Now
                  </Button>
                }
              />
            </div>
          ) : (
            <MessageView
              deskId={deskId}
              person_id={person_id}
              focusedMessageId={focusedMessageId} // highlights + scrolls to this message
              toggleThreadPanel={() => setOpenThreadPanel((prev) => !prev)}
              isPanelOpen={isThreadPanelOpen}
              desk_id={deskId}
              isPersonListOpen={isPersonListOpen}
              openPersonList={() => setIsPersonListOpen(true)}
              orgCode={selectedOrg as string}
              selectedChannels={selectedChannels}
            />
          )}

          <ThreadPanel
            focusedMessageId={focusedMessageId} // highlights active item in thread list
            onMessageFocus={setFocusedMessageId} // called when user clicks a message row
            isOpen={isThreadPanelOpen}
            onClose={() => setOpenThreadPanel(false)}
          />

          <AccountsPanel open={showAccountsPanel} onClose={() => setShowAccountsPanel(false)} />
          <ComposeModal type={composeModal} onClose={() => setComposeModal(null)} />
          {syncModal && (
            <SyncModal open={true} onClose={() => setSyncModal(null)} channel={syncModal} deskId={deskId ?? ""} />
          )}
        </div>
      )}
    </DashboardLayout>
  );
};

export default UnifiedCommsApp;
